import React, { useContext, useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import ComsEmbedPanel from './ComsEmbedPanel';
import api from '../utils/api';
import { isComsHubUser } from '../utils/comsHub';
import './Header.css';

const Header = () => {
  const { currentUser, loading, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [hasNewDevNotes, setHasNewDevNotes] = useState(false);
  const [showComsPanel, setShowComsPanel] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const comsPanelRef = useRef(null);
  const [allowedComsUserTypes, setAllowedComsUserTypes] = useState(null);
  const [comsEnabled, setComsEnabled] = useState(true);
  const apiBase = process.env.REACT_APP_API_BASE || '';
  const appEnv = (process.env.REACT_APP_ENV || '').toLowerCase();
  const isDev = process.env.NODE_ENV !== 'production';
  const isBeta = appEnv === 'beta' || /beta/i.test(apiBase);
  const notices = [
    isBeta ? 'BETA' : null,
    isDev ? 'DEV' : null
  ].filter(Boolean);
  const preferredComsChannel = currentUser?.userType === 'staff' || currentUser?.userType === 'admin' ? 'staff' : 'dev';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getDevNotesSeenKey = () => {
    const userId = currentUser?._id || currentUser?.id || 'unknown';
    return `devNotesLastSeenAt:${userId}`;
  };

  const markDevNotesSeen = () => {
    if (currentUser?.userType !== 'dev') return;
    localStorage.setItem(getDevNotesSeenKey(), new Date().toISOString());
    setHasNewDevNotes(false);
  };

  // Close Coms panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (comsPanelRef.current && !comsPanelRef.current.contains(e.target)) {
        setShowComsPanel(false);
      }
    };

    if (showComsPanel) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showComsPanel]);

  // Fetch connector config to check which user types can access ComSapp
  useEffect(() => {
    if (!currentUser) return;
    api.get('/api/connector/config')
      .then((res) => {
        const cfg = res.data?.config;
        setComsEnabled(cfg?.comsappEnabled !== false);
        const types = Array.isArray(cfg?.allowedUserTypes) && cfg.allowedUserTypes.length > 0
          ? cfg.allowedUserTypes
          : ['user', 'citizen', 'admin', 'education', 'staff'];
        setAllowedComsUserTypes(types);
      })
      .catch(() => {
        setComsEnabled(true);
        setAllowedComsUserTypes(['user', 'citizen', 'admin', 'education', 'staff']);
      });
  }, [currentUser]);

  useEffect(() => {
    let intervalId;

    const checkForNewDevNotes = async () => {
      if (loading || currentUser?.userType !== 'dev') {
        setHasNewDevNotes(false);
        return;
      }

      try {
        const response = await api.get('/api/developer-notes');
        const notes = Array.isArray(response.data) ? response.data : [];
        const newestTimestamp = notes.reduce((latest, note) => {
          const value = note?.publishedAt || note?.createdAt;
          const time = value ? new Date(value).getTime() : 0;
          return time > latest ? time : latest;
        }, 0);

        if (!newestTimestamp) {
          setHasNewDevNotes(false);
          return;
        }

        const lastSeenValue = localStorage.getItem(getDevNotesSeenKey());
        const lastSeenTime = lastSeenValue ? new Date(lastSeenValue).getTime() : 0;
        setHasNewDevNotes(newestTimestamp > lastSeenTime);
      } catch (error) {
        console.error('Failed to check dev note notifications:', error);
      }
    };

    checkForNewDevNotes();
    intervalId = window.setInterval(checkForNewDevNotes, 60000);

    return () => {
      if (intervalId) {
        window.clearInterval(intervalId);
      }
    };
  }, [currentUser, loading]);

  return (
    <div className="app-header-wrapper">
      {notices.length > 0 && (
        <div className="environment-banner">
          {notices.map((label) => (
            <span key={label} className={`environment-badge environment-badge-${label.toLowerCase()}`}>
              {label}
            </span>
          ))}
          <span className="environment-text">Environment notice</span>
        </div>
      )}
      <header className="app-header">
        <div className="logo">
          <Link to="/">
            <picture>
              <source media="(max-width: 640px)" srcSet="/vertical-logo.png" />
              <img src="/logo.jpg" alt="RSMIN Education Grant Portal" className="header-logo" />
            </picture>
          </Link>
        </div>

        <button
          type="button"
          className="header-menu-toggle"
          aria-label="Toggle navigation menu"
          aria-expanded={mobileMenuOpen}
          onClick={() => setMobileMenuOpen((open) => !open)}
        >
          <span></span><span></span><span></span>
        </button>

        <nav className={`header-nav${mobileMenuOpen ? ' header-nav--open' : ''}`}>
          <ul className="header-nav-list" onClick={() => setMobileMenuOpen(false)}>
          <li className="header-nav-item">
            <Link to="/">Home</Link>
          </li>
          <li className="header-nav-item">
            <a href="https://rsmin.ca/" target="_blank" rel="noreferrer">RSMIN Home</a>
          </li>
          
          {!loading && currentUser ? (
            <>
              {(currentUser.userType === 'staff' || currentUser.userType === 'admin') ? (
                <>
                  <li className="header-nav-item">
                    <Link to="/staff-dashboard">Workspace</Link>
                  </li>
                  <li className="header-nav-item">
                    <Link to="/citizen-dashboard">Public Portal</Link>
                  </li>
                </>
              ) : (
                <li className="header-nav-item">
                  <Link to="/dashboard">My Services</Link>
                </li>
              )}

              {currentUser.userType === 'admin' && (
                <li className="header-nav-item">
                  <Link to="/admin-dashboard">Admin Console</Link>
                </li>
              )}
              {comsEnabled && isComsHubUser(currentUser.userType) && allowedComsUserTypes?.includes(currentUser.userType) && (
                <li className="header-nav-item header-nav-item-coms-dropdown" ref={comsPanelRef}>
                  <button
                    className="coms-header-btn"
                    onClick={() => setShowComsPanel(!showComsPanel)}
                    title="Messages"
                  >
                    💬 Coms
                  </button>
                  {showComsPanel && (
                    <div className="coms-header-panel">
                      <ComsEmbedPanel
                        channel={preferredComsChannel}
                        compact={true}
                        onOpenInComSapp={() => setShowComsPanel(false)}
                      />
                    </div>
                  )}
                </li>
              )}
              {comsEnabled && allowedComsUserTypes?.includes(currentUser.userType) && (
                <li className="header-nav-item">
                  <Link to="/coms-hub" className="coms-full-link">Full Coms Hub</Link>
                </li>
              )}
              {currentUser.userType === 'dev' && (
                <li className="header-nav-item">
                  <Link to="/dev-notes" className="dev-notice-link" onClick={markDevNotesSeen}>
                    <i className="fas fa-bell dev-notice-bell" aria-hidden="true"></i>
                    <span>Dev Notes</span>
                    {hasNewDevNotes && (
                      <span className="dev-notice-dot" aria-label="New dev note available"></span>
                    )}
                  </Link>
                </li>
              )}
              <li className="header-nav-item">
                <span className="user-name">{currentUser.firstName} {currentUser.lastName}</span>
                <Link to="/account" className="account-link">My Account</Link>
                <button onClick={handleLogout} className="logout-button">Logout</button>
              </li>
            </>
          ) : (
            <>
              <li className="header-nav-item">
                <Link to="/login">Login</Link>
              </li>
              <li className="header-nav-item">
                <Link to="/register">Register</Link>
              </li>
            </>
          )}
          </ul>
        </nav>
      </header>
    </div>
  );
};

export default Header;